import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilePlus2, Pin, Search, Star, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import type { LearningProject, Note, NoteInput, NoteKind } from "../lib/contracts";
import { endpoints, entityEndpoint } from "../lib/contracts";
import { ApiError, request } from "../lib/api";
import { formatRelative } from "../lib/format";
import { useCollection } from "../hooks/useCollection";
import { useOnline } from "../hooks/useOnline";
import { useToast } from "../contexts/ToastContext";
import { EmptyState, ErrorState, LoadingState } from "../components/ui/AsyncState";
import { ConfirmDialog, Dialog } from "../components/ui/Dialog";
import { PageHeader } from "../components/ui/PageHeader";
import { SaveIndicator, type SaveState, StatusBadge } from "../components/ui/Status";

interface NoteDraft extends NoteInput { id?: string; version?: number; }
const emptyDraft = (kind: NoteKind): NoteDraft => ({ kind, title: "", body: "", category: "", learningProjectId: null, isPinned: false, isFavorite: false, isDraft: true });

function MarkdownPreview({ source }: { source: string }) {
  if (!source.trim()) return <p className="text-sm text-muted">暂无预览内容。</p>;
  return (
    <div className="space-y-3 break-words text-sm leading-relaxed">
      {source.split(/\n{2,}/).map((block, index) => {
        if (block.startsWith("### ")) return <h4 key={index} className="text-lg font-semibold">{block.slice(4)}</h4>;
        if (block.startsWith("## ")) return <h3 key={index} className="text-xl font-semibold">{block.slice(3)}</h3>;
        if (block.startsWith("# ")) return <h2 key={index} className="text-2xl font-bold">{block.slice(2)}</h2>;
        if (block.split("\n").every((line) => line.startsWith("- "))) return <ul key={index} className="list-disc space-y-1 pl-5">{block.split("\n").map((line) => <li key={line}>{line.slice(2)}</li>)}</ul>;
        return <p key={index} className="whitespace-pre-wrap">{block}</p>;
      })}
    </div>
  );
}

export function NotesPage({ kind }: { kind: NoteKind }) {
  const params = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const { notify } = useToast();
  const basePath = kind === "work" ? "/work/notes" : "/growth/notes";
  const title = kind === "work" ? "工作笔记" : "学习笔记";
  const { items, setItems, loading, error, reload } = useCollection<Note>(endpoints.notes, { kind });
  const projects = useCollection<LearningProject>(endpoints.learningProjects, { status: "active" });
  const [query, setQuery] = useState("");
  const [draft, setDraftState] = useState<NoteDraft>(() => emptyDraft(kind));
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [preview, setPreview] = useState(false);
  const [conflict, setConflict] = useState<Note | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const latestRef = useRef(draft);
  const revisionRef = useRef(0);
  const savingRef = useRef(false);
  const queuedRef = useRef(false);
  const dirtyRef = useRef(false);
  const saveRef = useRef<() => Promise<void>>(async () => undefined);
  const activeId = params.id;
  const creating = activeId === "new";
  const active = items.find((item) => item.id === activeId);

  const updateDraft = (values: Partial<NoteDraft>) => {
    revisionRef.current += 1;
    dirtyRef.current = true;
    setSaveState("dirty");
    setDraftState((current) => {
      const next = { ...current, ...values };
      latestRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    if (creating) {
      const next = emptyDraft(kind); latestRef.current = next; setDraftState(next); setSaveState("idle"); dirtyRef.current = false;
    } else if (active && !dirtyRef.current) {
      const next: NoteDraft = { id: active.id, version: active.version, kind: active.kind, title: active.title, body: active.body, category: active.category ?? "", learningProjectId: active.learningProjectId, isPinned: active.isPinned, isFavorite: active.isFavorite, isDraft: active.isDraft };
      latestRef.current = next; setDraftState(next); setSaveState("saved");
    }
  }, [creating, active, kind]);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => { if (dirtyRef.current) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const saveNow = useCallback(async () => {
    if (!dirtyRef.current || !online) { if (!online && dirtyRef.current) setSaveState("error"); return; }
    if (savingRef.current) { queuedRef.current = true; return; }
    const snapshot = latestRef.current;
    if (!snapshot.title.trim()) { setSaveState("dirty"); return; }
    const snapshotRevision = revisionRef.current;
    savingRef.current = true; queuedRef.current = false; setSaveState("saving");
    try {
      const payload: NoteInput = { kind, title: snapshot.title.trim(), body: snapshot.body ?? "", category: snapshot.category || null, learningProjectId: kind === "learning" ? snapshot.learningProjectId ?? null : null, isPinned: snapshot.isPinned, isFavorite: snapshot.isFavorite, isDraft: snapshot.isDraft, ...(snapshot.id ? { version: snapshot.version } : {}) };
      const saved = await request<Note>(snapshot.id ? entityEndpoint("notes", snapshot.id) : endpoints.notes, { method: snapshot.id ? "PATCH" : "POST", body: payload });
      setItems((current) => {
        const rest = current.filter((item) => item.id !== saved.id);
        return [saved, ...rest];
      });
      if (!snapshot.id) navigate(`${basePath}/${saved.id}`, { replace: true });
      setDraftState((current) => {
        const next = { ...current, id: saved.id, version: saved.version };
        latestRef.current = next;
        return next;
      });
      if (revisionRef.current === snapshotRevision) { dirtyRef.current = false; setSaveState("saved"); }
      else queuedRef.current = true;
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 409) setConflict(reason.current as Note);
      setSaveState("error");
    } finally {
      savingRef.current = false;
      if (queuedRef.current && !conflict) window.setTimeout(() => void saveRef.current(), 0);
    }
  }, [basePath, kind, navigate, online, setItems, conflict]);
  saveRef.current = saveNow;
  useEffect(() => {
    if (!dirtyRef.current) return;
    const timer = window.setTimeout(() => void saveRef.current(), 900);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const filtered = useMemo(() => {
    const value = query.trim().toLocaleLowerCase();
    return value ? items.filter((item) => `${item.title} ${item.body} ${item.category ?? ""}`.toLocaleLowerCase().includes(value)) : items;
  }, [items, query]);
  const choose = (id: string) => {
    if (dirtyRef.current && !window.confirm("当前笔记还有未保存修改。确定离开吗？")) return;
    dirtyRef.current = false; navigate(`${basePath}/${id}`);
  };
  const remove = async () => {
    if (!draft.id) { navigate(basePath); return; }
    setDeleteBusy(true);
    try { await request<void>(entityEndpoint("notes", draft.id), { method: "DELETE" }); notify("笔记已移到回收站。"); dirtyRef.current = false; setDeleteOpen(false); navigate(basePath); await reload(); }
    catch (reason) { notify(reason instanceof Error ? reason.message : "删除失败。", "error"); }
    finally { setDeleteBusy(false); }
  };
  const useCloud = () => {
    if (!conflict) return;
    const next: NoteDraft = { id: conflict.id, version: conflict.version, kind: conflict.kind, title: conflict.title, body: conflict.body, category: conflict.category ?? "", learningProjectId: conflict.learningProjectId, isPinned: conflict.isPinned, isFavorite: conflict.isFavorite, isDraft: conflict.isDraft };
    latestRef.current = next; setDraftState(next); dirtyRef.current = false; setSaveState("saved"); setConflict(null);
  };

  return (
    <div className="page-container">
      <PageHeader title={title} description={kind === "work" ? "用 Markdown 整理工作上下文，停笔约 900ms 后自动保存。" : "把学习中的理解、问题和结论持续沉淀下来。"} action={<button className="btn-primary w-full sm:w-auto" disabled={!online} onClick={() => navigate(`${basePath}/new`)}><FilePlus2 size={18} />新建笔记</button>} />
      {loading ? <LoadingState /> : error ? <ErrorState message={error} onRetry={() => void reload()} /> : (
        <div className="card grid min-h-[32rem] overflow-hidden md:grid-cols-[20rem_minmax(0,1fr)]">
          <aside className={`${activeId ? "hidden md:block" : "block"} border-r`} aria-label={`${title}列表`}>
            <div className="border-b p-3"><label className="relative block"><span className="sr-only">搜索笔记</span><Search className="absolute left-3 top-3 text-muted" size={18} /><input className="input pl-10" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索标题与正文" /></label></div>
            <div className="max-h-[38rem] overflow-y-auto p-2">
              {filtered.length === 0 ? <div className="p-5 text-center text-sm text-muted">{query ? "没有符合条件的笔记" : "还没有笔记"}</div> : filtered.map((item) => (
                <button key={item.id} className={`mb-1 w-full rounded-lg p-3 text-left transition-colors ${item.id === activeId ? "bg-[var(--color-primary-soft)]" : "hover:bg-subtle"}`} onClick={() => choose(item.id)}>
                  <span className="flex items-center gap-2 font-semibold"><span className="min-w-0 flex-1 truncate">{item.title}</span>{item.isPinned ? <Pin size={14} aria-label="已置顶" /> : null}{item.isFavorite ? <Star size={14} aria-label="已收藏" /> : null}</span>
                  <span className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted">{item.body || "暂无正文"}</span>
                  <span className="mt-2 block text-xs text-[var(--color-text-subtle)]">{formatRelative(item.updatedAt)}{item.isDraft ? " · 草稿" : ""}</span>
                </button>
              ))}
            </div>
          </aside>
          <section className={`${activeId ? "block" : "hidden md:block"} min-w-0 p-4 md:p-6`}>
            {!activeId ? <EmptyState title="选择一篇笔记" description="从左侧选择笔记，或创建一篇新的笔记。" /> : (
              <div className="mx-auto max-w-reading">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <button className="btn-ghost md:hidden" onClick={() => navigate(basePath)}>返回列表</button>
                  <SaveIndicator state={saveState} onRetry={() => void saveNow()} />
                  <div className="ml-auto flex gap-1"><button className={`btn-ghost ${preview ? "" : "bg-subtle text-ink"}`} onClick={() => setPreview(false)} aria-pressed={!preview}>编辑</button><button className={`btn-ghost ${preview ? "bg-subtle text-ink" : ""}`} onClick={() => setPreview(true)} aria-pressed={preview}>预览</button><button className="icon-btn text-danger" onClick={() => setDeleteOpen(true)} aria-label="删除这篇笔记"><Trash2 size={18} /></button></div>
                </div>
                {saveState === "error" ? <div className="mb-4 rounded-lg bg-[var(--color-danger-soft)] p-3 text-sm text-danger" role="alert">内容仍保留在此页面，但尚未保存到云端。请检查网络后重试。</div> : null}
                <label className="field"><span className="field-label">标题（必填）</span><input className="input text-lg font-semibold" value={draft.title} onChange={(event) => updateDraft({ title: event.target.value })} disabled={!online && saveState !== "dirty"} /></label>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="field"><span className="field-label">分类</span><input className="input" value={draft.category ?? ""} onChange={(event) => updateDraft({ category: event.target.value })} /></label>
                  {kind === "learning" ? <label className="field"><span className="field-label">关联学习项目</span><select className="input" value={draft.learningProjectId ?? ""} onChange={(event) => updateDraft({ learningProjectId: event.target.value || null })}><option value="">不关联</option>{projects.items.map((project) => <option key={project.id} value={project.id}>{project.title}</option>)}</select></label> : null}
                </div>
                <div className="mt-4 flex flex-wrap gap-4"><label className="inline-flex min-h-11 items-center gap-2"><input type="checkbox" checked={draft.isPinned ?? false} onChange={(event) => updateDraft({ isPinned: event.target.checked })} />置顶</label><label className="inline-flex min-h-11 items-center gap-2"><input type="checkbox" checked={draft.isFavorite ?? false} onChange={(event) => updateDraft({ isFavorite: event.target.checked })} />收藏</label><label className="inline-flex min-h-11 items-center gap-2"><input type="checkbox" checked={draft.isDraft ?? false} onChange={(event) => updateDraft({ isDraft: event.target.checked })} />草稿</label></div>
                <div className="mt-4"><span className="field-label">正文（Markdown）</span>{preview ? <div className="mt-1 min-h-80 rounded-lg border bg-surface p-5"><MarkdownPreview source={draft.body ?? ""} /></div> : <textarea className="textarea mt-1 min-h-80 font-mono text-sm" value={draft.body ?? ""} onChange={(event) => updateDraft({ body: event.target.value })} aria-label="笔记正文" />}</div>
              </div>
            )}
          </section>
        </div>
      )}
      <ConfirmDialog open={deleteOpen} title={`删除“${draft.title || "这篇笔记"}”`} description="笔记会移到回收站，可在设置中恢复。" busy={deleteBusy} onClose={() => setDeleteOpen(false)} onConfirm={remove} />
      <Dialog open={Boolean(conflict)} title="笔记已在其他设备修改" description="本地草稿不会被自动覆盖。请比较后选择要保留的版本。" onClose={() => setConflict(null)} wide footer={<><button className="btn-secondary" onClick={() => setConflict(null)}>继续保留本地草稿</button><button className="btn-primary" onClick={useCloud}>使用云端版本</button></>}>
        <div className="grid gap-4 md:grid-cols-2"><section className="rounded-lg border p-4"><h3 className="font-semibold">本地草稿</h3><pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap text-sm text-muted">{draft.body}</pre></section><section className="rounded-lg border p-4"><h3 className="font-semibold">云端版本</h3><pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap text-sm text-muted">{conflict?.body}</pre></section></div>
      </Dialog>
    </div>
  );
}
